import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import Header from '../../components/ui/Header';
import ContractTable from './components/ContractTable';
import NotificationToast from '../../components/ui/NotificationToast';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { generateContractDisplayName } from '../../utils/contractNaming';

const ContractHistory = () => {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [selectedContracts, setSelectedContracts] = useState([]);
  const [sortConfig, setSortConfig] = useState({ column: 'uploadDate', direction: 'desc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Load real contract data from Supabase milestones table
  useEffect(() => {
    loadContractHistory();
  }, []);

  const loadContractHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      // Query milestones table and get unique deals
      const { data: milestones, error: milestonesError } = await supabase
        .from('milestones')
        .select('*, deals:deal_id(id, property_street, city, state, created_at)')
        .order('created_at', { ascending: false });

      if (milestonesError) throw milestonesError;

      // Load draft snapshots to get party info and client_type
      const { data: draftSnapshots, error: snapshotError } = await supabase
        .from('draft_snapshots')
        .select('deal_id, selected_data')
        .order('created_at', { ascending: false });

      if (snapshotError) console.warn('Could not load draft snapshots:', snapshotError);

      // Create a map of deal_id to latest snapshot data
      const snapshotMap = new Map();
      if (draftSnapshots) {
        draftSnapshots.forEach(snapshot => {
          if (!snapshotMap.has(snapshot.deal_id)) {
            snapshotMap.set(snapshot.deal_id, snapshot.selected_data);
          }
        });
      }

      // Group milestones by deal_id to get unique contracts
      const dealsMap = new Map();

      milestones.forEach(milestone => {
        if (milestone.deal_id && milestone.deals) {
          const dealId = milestone.deal_id;
          if (!dealsMap.has(dealId)) {
            const snapshotData = snapshotMap.get(dealId) || {};
            const propertyAddress = `${milestone.deals.property_street || 'Unknown'}, ${milestone.deals.city || ''}`;

            // Generate display name using client info from snapshot
            const nameInfo = generateContractDisplayName(
              snapshotData.clientType,
              snapshotData.partyInfo,
              propertyAddress
            );

            dealsMap.set(dealId, {
              id: milestone.deals.id,
              deal_id: dealId,
              filename: nameInfo.displayName, // Use formatted name instead of file path
              displayName: nameInfo.displayName,
              clientName: snapshotData.partyInfo?.buyer || snapshotData.partyInfo?.seller || null,
              clientType: snapshotData.clientType,
              propertyAddress: propertyAddress,
              uploadDate: new Date(milestone.deals.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              }),
              status: 'processed',
              lastActivity: getTimeAgo(milestone.deals.created_at),
              fileSize: '2.4 MB',
              processingTime: '45 seconds',
              created_at: milestone.deals.created_at
            });
          }
        }
      });

      const contractsArray = Array.from(dealsMap.values());
      setContracts(contractsArray);

      if (contractsArray.length === 0) {
        addNotification({
          type: 'info',
          title: 'No Contracts Found',
          message: 'No contracts in history yet. Upload one to get started.',
          duration: 3000
        });
      }
    } catch (err) {
      console.error('Error loading contract history:', err);
      setError(err.message);
      addNotification({
        type: 'error',
        title: 'Load Error',
        message: 'Failed to load contract history: ' + err.message,
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const addNotification = (notification) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { ...notification, id }]);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Auto-remove notifications
  useEffect(() => {
    notifications.forEach(notification => {
      if (notification.duration) {
        setTimeout(() => {
          removeNotification(notification.id);
        }, notification.duration);
      }
    });
  }, [notifications]);

  // Handle sorting
  const handleSort = (newSortConfig) => {
    setSortConfig(newSortConfig);
  };

  // Handle contract actions
  const handleRowClick = (contractId) => {
    // Navigate to contract summary page
    navigate(`/contract/${contractId}`);
  };

  const handleViewAnalysis = (contractId) => {
    // Navigate to contract summary page
    navigate(`/contract/${contractId}`);
  };

  const handleRegenerateEmail = (contractId) => {
    navigate('/email-template-generator', { state: { contractId } });
  };

  const handleDownloadDocument = async (contractId) => {
    try {
      const contract = contracts.find(c => c.id === contractId);
      if (!contract) return;

      // Fetch the contract PDF from Supabase storage
      const { data, error } = await supabase.storage
        .from('contracts')
        .download(`contract_${contractId}.pdf`);

      if (error) {
        // If file not found in storage, create a simple PDF download
        const element = document.createElement('a');
        const file = new Blob(['Contract document'], { type: 'application/pdf' });
        element.href = URL.createObjectURL(file);
        element.download = contract.filename || 'contract.pdf';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
        URL.revokeObjectURL(element.href);
      } else {
        // File exists in storage, download it
        const url = URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.download = contract.filename || 'contract.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      addNotification({
        type: 'success',
        title: 'Download Complete',
        message: `${contract.filename} downloaded successfully`,
        duration: 3000
      });
    } catch (err) {
      console.error('Error downloading document:', err);
      addNotification({
        type: 'error',
        title: 'Download Failed',
        message: 'Failed to download document',
        duration: 3000
      });
    }
  };

  const handleDownloadCalendar = async (contractId) => {
    try {
      const contract = contracts.find(c => c.id === contractId);
      if (!contract) return;

      // Create ICS calendar file content
      const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Concal//Contract Dates//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
BEGIN:VEVENT
UID:${contractId}-${Date.now()}@concal.app
DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z
SUMMARY:Contract Deadline - ${contract.propertyAddress}
DESCRIPTION:Contract for property: ${contract.propertyAddress}
STATUS:CONFIRMED
END:VEVENT
END:VCALENDAR`;

      // Create blob and download
      const blob = new Blob([icsContent], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${contract.filename?.replace('.pdf', '') || 'contract'}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      addNotification({
        type: 'success',
        title: 'Calendar File Downloaded',
        message: `ICS file for ${contract.propertyAddress} downloaded successfully`,
        duration: 3000
      });
    } catch (err) {
      console.error('Error downloading calendar:', err);
      addNotification({
        type: 'error',
        title: 'Download Failed',
        message: 'Failed to download calendar file',
        duration: 3000
      });
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedContracts.length === 0) {
      addNotification({
        type: 'warning',
        title: 'No Selection',
        message: 'Please select contracts to delete',
        duration: 3000
      });
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedContracts.length} contract(s)? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    try {
      // Delete from deals table (this will cascade to milestones)
      const { error } = await supabase
        .from('deals')
        .delete()
        .in('id', selectedContracts);

      if (error) throw error;

      // Remove deleted contracts from state
      setContracts(contracts.filter(c => !selectedContracts.includes(c.id)));
      setSelectedContracts([]);

      addNotification({
        type: 'success',
        title: 'Deleted',
        message: `${selectedContracts.length} contract(s) deleted successfully`,
        duration: 3000
      });
    } catch (err) {
      console.error('Error deleting contracts:', err);
      addNotification({
        type: 'error',
        title: 'Delete Failed',
        message: 'Failed to delete contracts: ' + err.message,
        duration: 3000
      });
    } finally {
      setDeleting(false);
    }
  };

  // Filter contracts based on search term
  const filteredContracts = useMemo(() => {
    if (!searchTerm) return contracts;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return contracts.filter(contract =>
      contract.filename?.toLowerCase().includes(lowerSearchTerm) ||
      contract.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
      contract.uploadDate?.toLowerCase().includes(lowerSearchTerm)
    );
  }, [contracts, searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredContracts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedContracts = filteredContracts.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="pt-20">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                  Contract History
                </h1>
                <p className="text-muted-foreground">
                  Track and manage all uploaded real estate contracts.
                </p>
              </div>

              <div className="hidden sm:flex items-center space-x-3">
                <Button
                  variant="default"
                  onClick={() => navigate('/contract-upload')}
                  iconName="Upload"
                  iconPosition="left"
                  iconSize={16}
                >
                  Upload Contract
                </Button>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <Icon name="Loader" size={32} className="mx-auto mb-4 animate-spin" color="var(--color-primary)" />
              <p className="text-foreground">Loading contract history...</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-card border border-red-200 rounded-lg p-8 text-center mb-6">
              <Icon name="AlertCircle" size={32} className="mx-auto mb-4" color="#ef4444" />
              <h3 className="text-lg font-semibold text-red-600 mb-2">Error Loading Contracts</h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button
                variant="outline"
                onClick={loadContractHistory}
                iconName="RotateCw"
                iconPosition="left"
              >
                Retry
              </Button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && contracts.length === 0 && (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <Icon name="FileText" size={48} className="mx-auto mb-4" color="var(--color-muted-foreground)" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No Contracts Yet</h3>
              <p className="text-muted-foreground mb-6">
                Start by uploading a real estate contract to begin tracking.
              </p>
              <Button
                variant="default"
                onClick={() => navigate('/contract-upload')}
                iconName="Upload"
                iconPosition="left"
              >
                Upload Your First Contract
              </Button>
            </div>
          )}

          {/* Contract Table */}
          {!loading && !error && contracts.length > 0 && (
            <>
              {/* Search and Actions Bar */}
              <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="flex-1 w-full">
                  <input
                    type="text"
                    placeholder="Search contracts by name, address, or date..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1); // Reset to first page on search
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {selectedContracts.length > 0 && (
                  <Button
                    variant="destructive"
                    onClick={handleDeleteSelected}
                    disabled={deleting}
                    iconName="Trash2"
                    iconPosition="left"
                    className="whitespace-nowrap"
                  >
                    Delete ({selectedContracts.length})
                  </Button>
                )}
              </div>

              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <Icon name="Info" size={16} className="text-blue-600 mt-1 flex-shrink-0" />
                  <p className="text-sm text-blue-700">
                    {searchTerm ? (
                      <>Showing {filteredContracts.length} matching contract{filteredContracts.length !== 1 ? 's' : ''}</>
                    ) : (
                      <>Showing {contracts.length} contract{contracts.length !== 1 ? 's' : ''} from your upload history</>
                    )}
                  </p>
                </div>
              </div>

              <ContractTable
                contracts={paginatedContracts}
                selectedContracts={selectedContracts}
                onSelectionChange={setSelectedContracts}
                onSort={handleSort}
                sortConfig={sortConfig}
                onViewAnalysis={handleViewAnalysis}
                onRegenerateEmail={handleRegenerateEmail}
                onDownloadDocument={handleDownloadDocument}
                onDownloadCalendar={handleDownloadCalendar}
                onRowClick={handleRowClick}
              />

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, contracts.length)} of {contracts.length} contracts
                  </p>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-2 bg-card border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-foreground">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 bg-card border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Mobile Action Button */}
          <div className="sm:hidden fixed bottom-6 right-6">
            <Button
              variant="default"
              size="lg"
              onClick={() => navigate('/contract-upload')}
              iconName="Upload"
              iconSize={20}
              className="rounded-full shadow-lg"
            />
          </div>
        </div>
      </div>

      {/* Notifications */}
      <NotificationToast
        notifications={notifications}
        onDismiss={removeNotification}
        maxVisible={3}
      />
    </div>
  );
};

export default ContractHistory;
